import React, { useState, useRef, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ExpandCircleDownIcon from "@mui/icons-material/ExpandCircleDown";
import { useGlobalContext } from "../../../Context/GlobalContext";
import Cookies from "js-cookie";

const PayrollFinalizedSidebar = ({
  toggleOffCanvas,
  setIsOffCanvasOpen,
  isOffCanvasOpen,
  data,
  fetchPayrollDetails,
  selectedPayroll,
}) => {
  const { openToast, baseUrl } = useGlobalContext();
  const [isEarningsOpen, setIsEarningsOpen] = useState(false);
  const [isDeductionsOpen, setIsDeductionsOpen] = useState(false);
  const [isPaidAmount, setIsPaidAmount] = useState(false);
  const [isFinalized, setIsFinalized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (data) {
      setIsFinalized(
        data?.staffDetails?.SalaryDetails[0]?.payroll_finalized ? true : false
      );
    }
  }, [data]);

  const finalizePayroll = async () => {
    try {
      setIsLoading(true);
      const id = data?.staffDetails?.SalaryDetails[0]?.id;
      if (!id) {
        openToast("Salary details not found", "error");
        return;
      }
      const response = await fetch(`${baseUrl}payroll/finalize/` + id, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Cookies.get("flowChangerAuthToken")}`,
        },
        body: JSON.stringify({
          salary: parseFloat(data.total),
          payroll: !isFinalized,
        }),
      });
      if (response.status == 200) {
        const data = await response.json();
        openToast(data.message, "success");
        setIsFinalized(!isFinalized);
        fetchPayrollDetails();
        setIsOffCanvasOpen();
      } else {
        openToast("Something went wrong", "error");
      }
    } catch (error) {
      openToast("Something went wrong", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const sampleData = {
    month: "November",
    year: 2024,
    employeeName: "Kanika aroa",
    totalSalary: 13333.34,
    earnings: [
      { label: "Basic Salary", amount: 7166.67 },
      { label: "Work Basis Payment", amount: 0, hasChildren: true },
      { label: "Incentives", amount: 0, hasChildren: true },
      { label: "Reimbursement", amount: 0, hasChildren: true },
      { label: "Bonus", amount: 0, hasChildren: true },
      { label: "Overtime Pay", amount: 0, hasChildren: true },
      { label: "Other Earnings", amount: 0, hasChildren: true },
    ],
    deductions: [
      { label: "Late Fine", amount: 1093.0 },
      { label: "Early Fine", amount: 1000.0 },
      { label: "Other Deductions", amount: 1000.0 },
      { label: "Loan Payment", amount: 1000.0 },
    ],
    paidAmount: [
      {
        label: "Advance Given",
        amount:
          selectedPayroll?.staffDetails?.SalaryDetails[0]?.PaymentHistory?.map(
            (item) => {
              return item?.type == "ADVANCE" ? item?.amount : 0;
            }
          ),
      },
      { label: "Salary Paid", amount: 1000.0 },
    ],
  };
  const sidebarRef = useRef(null);

  // Toggle the sidebar off when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsOffCanvasOpen(false);
      }
    }

    if (isOffCanvasOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOffCanvasOpen, setIsOffCanvasOpen]);

  const handleTableRowClick = () => {
    setIsOffCanvasOpen(true);
  };
  return (
    <div
    ref={sidebarRef}
    className={`fixed top-[56px] right-0 h-screen sm:w-[400px] w-full z-[99] shadow-cs bg-white payroll-sidebar shadow-lg transform transition-transform duration-300 ${
        isOffCanvasOpen ? 'translate-x-0' : 'translate-x-full'
    }`}

    >
      <div className="relative w-full h-full">
        <div className="flex justify-between items-center border-gray-200 p-4 border-b">
          <h2 className="font-semibold text-[#000] text-[18px]">
            {data?.name}
          </h2>
          <button className="text-[black]" onClick={setIsOffCanvasOpen}>
            <CloseIcon />
          </button>
        </div>
        <div className="relative z-[1] bg-red mb-[10px] p-[10px] pb-[20px] w-full max-w-md h-[82%] overflow-y-scroll">
          <div className="flex justify-between items-center bg-[#e5e7eb96] mb-[10px] p-[10px] pr-[42px] w-full">
            <p className="font-normal text-muted-foreground text-sm">
              Total Salary
            </p>
            <p className="font-normal text-muted-foreground text-sm">
              ₹ {parseInt(data?.total || 0).toFixed(2)}
            </p>
          </div>
          <div className="flex justify-between items-center bg-[#fff9e9] mb-[10px] p-3 w-full">
            <div className="font-medium text-gray-900 text-sm">
              {isFinalized ? (
                <div className="flex items-center gap-2 w-[70%]">
                  <svg
                    viewBox="0 0 24 24"
                    className="w-5 h-5 text-green-500"
                    fill="currentColor"
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                  <span className="text-gray-900">
                    Finalized on{" "}
                    {new Date(
                      data.staffDetails.SalaryDetails[0].finalized_date
                    ).toDateString()}
                  </span>
                </div>
              ) : (
                "Payroll Finalized: No"
              )}
            </div>
            <button
              onClick={() => finalizePayroll()}
              disabled={isLoading}
              className="bg-[#27004a] hover:bg-[#50207b] disabled:opacity-70 px-2 py-2 rounded focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-normal text-sm text-white cursor-pointer disabled:cursor-not-allowed focus:outline-none"
            >
              {isFinalized
                ? isLoading
                  ? "Unfinalizing Payroll..."
                  : "Unfinalize Payroll"
                : isLoading
                ? "Finalizing Payroll..."
                : "Finalize Payroll"}
            </button>
          </div>

          {/* Earnings Section */}
          <div className="mb-[10px] border-b">
            <button
              onClick={() => setIsEarningsOpen(!isEarningsOpen)}
              className="flex justify-between items-center bg-[#e5e7eb96] p-[10px] w-full"
            >
              <span className="font-normal text-muted-foreground text-sm">
                Earnings
              </span>
              <div className="flex items-center gap-2">
                <span className="font-normal text-muted-foreground text-sm">
                  ₹{" "}
                  {sampleData.earnings
                    .reduce((acc, curr) => acc + curr.amount, 0)
                    .toFixed(2)}
                </span>
                <ExpandCircleDownIcon
                  className={`h-4 w-4 transition-all ease-in-out duration-200 ${
                    isEarningsOpen ? "rotate-180" : "rotate-0"
                  }`}
                />
              </div>
            </button>

            {isEarningsOpen && (
              <div
                className={`grid transition-all duration-200 ease-in-out
                                ${
                                  isEarningsOpen
                                    ? " grid-rows-[1fr]"
                                    : " grid-rows-[0fr]"
                                }`}
              >
                <div className="overflow-hidden">
                  {sampleData.earnings.map((item, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center px-[20px] py-2 pr-[43px] pb-[10px] border-b text-sm"
                    >
                      <span className="font-light text-muted-foreground">
                        {item.label}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="font-light">
                          ₹ {item.amount.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Deductions Section */}
          <div className="mb-[10px]">
            <button
              onClick={() => setIsDeductionsOpen(!isDeductionsOpen)}
              className="flex justify-between items-center bg-[#e5e7eb96] p-[10px] w-full"
            >
              <span className="font-normal text-muted-foreground text-sm">
                Deductions
              </span>
              <div className="flex items-center gap-2">
                <span className="font-normal text-muted-foreground text-sm">
                  ₹{" "}
                  {sampleData.deductions
                    .reduce((acc, curr) => acc + curr.amount, 0)
                    .toFixed(2)}
                </span>
                <ExpandCircleDownIcon
                  className={`h-4 w-4 transition-transform duration-200 ${
                    isDeductionsOpen ? "rotate-180" : "rotate-0"
                  }`}
                />
              </div>
            </button>

            {isDeductionsOpen && (
              <div
                className={
                  "grid transition-all duration-200 ease-in-out" +
                  isDeductionsOpen
                    ? "grid-rows-[1fr]"
                    : "grid-rows-[0fr]"
                }
              >
                <div className="overflow-hidden">
                  {sampleData.deductions.map((item, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center px-[20px] py-2 pr-[43px] pb-[10px] border-b text-sm"
                    >
                      <span className="font-light text-muted-foreground">
                        {item.label}
                      </span>
                      <span className="font-light text-muted-foreground">
                        ₹ {item.amount.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Paid Amount Section */}
          <div className="mb-[10px]">
            <button
              onClick={() => setIsPaidAmount(!isPaidAmount)}
              className="flex justify-between items-center bg-[#e5e7eb96] p-[10px] w-full"
            >
              <span className="font-normal text-muted-foreground text-sm">
                Paid Amount
              </span>
              <div className="flex items-center gap-2">
                <span className="font-normal text-muted-foreground text-sm">
                  ₹{" "}
                  {
                    sampleData?.paidAmount?.amount
                    // .reduce((acc, curr) => acc + curr.amount, 0)
                    // .toFixed(2)
                  }
                </span>
                <ExpandCircleDownIcon
                  className={`h-4 w-4 transition-transform duration-200 ${
                    isPaidAmount ? "rotate-180" : "rotate-0"
                  }`}
                />
              </div>
            </button>

            {isPaidAmount && (
              <div
                className={
                  "grid transition-all duration-200 ease-in-out" + isPaidAmount
                    ? "grid-rows-[1fr]"
                    : "grid-rows-[0fr]"
                }
              >
                <div className="overflow-hidden">
                  {sampleData.paidAmount.map((item, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center px-[20px] py-2 pr-[43px] pb-[10px] border-b text-sm"
                    >
                      <span className="font-light text-muted-foreground">
                        {item.label}
                      </span>
                      <span className="font-light text-muted-foreground">
                        ₹ {item.amount}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-between items-center bg-[#e5e7eb96] mb-[10px] p-[10px] pr-[42px] w-full">
            <p className="font-normal text-muted-foreground text-sm">Pending</p>
            <p className="font-normal text-muted-foreground text-sm">
              ₹ 0.0000
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayrollFinalizedSidebar;
